import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, fetchUserAttributes, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { apiConfig } from '../config/amplify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current session
      const session = await fetchAuthSession();
      
      if (!session.tokens) {
        setUser(null);
        setIsAdmin(false);
        return;
      }

      // Get user attributes
      const attributes = await fetchUserAttributes();
      
      // Extract groups from token
      const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
      const userIsAdmin = groups.includes(apiConfig.adminGroup);

      setUser({
        email: attributes.email,
        username: attributes['cognito:username'] || attributes.sub,
        sub: attributes.sub,
        groups: groups,
        attributes: attributes
      });
      
      setIsAdmin(userIsAdmin);

    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setIsAdmin(false);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Listen for auth events from Amplify
    const hubListener = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn' || event === 'tokenRefresh') {
        console.log('Auth event:', event);
        checkAuth();
      } else if (event === 'signedOut') {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => hubListener();
  }, [checkAuth]);

  const signOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIsAdmin(false);
    } catch (err) {
      console.error('Sign out failed:', err);
      throw err;
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (err) {
      console.error('Failed to get access token:', err);
      throw err;
    }
  };

  const value = {
    user,
    isAdmin,
    loading,
    error,
    signOut,
    refreshAuth: checkAuth,
    getAccessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
